"use client";

import type React from "react";

import { useState } from "react";
import { Button } from "@papier/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@papier/ui/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@papier/ui/components/ui/card";
import { Eye, Settings2 } from "lucide-react";
import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";
import { useSchema } from "@/hooks/use-schema";
import { useDragDrop } from "@/hooks/use-drag-drop";
import { generateZodSchema } from "@/utils/schema-utils";
import { FieldTypePalette } from "./field-type-palette";
import { SchemaCanvas } from "./schema-canvas";
import { PropertiesPanel } from "./properties-panel";

interface SchemaBuilderDialogProps {
  trigger?: React.ReactNode;
  initialSchema?: SchemaField[];
  onSave?: (schema: SchemaField[]) => void;
  title?: string;
}

export function SchemaBuilderDialog({
  trigger,
  initialSchema = [],
  onSave,
  title = "Schema Builder",
}: SchemaBuilderDialogProps) {
  const [selectedField, setSelectedField] = useState<SchemaField | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    schema,
    setSchema,
    addField,
    updateField,
    deleteField,
    moveField,
    updateArrayItemType,
    addFieldToParent,
  } = useSchema();

  const {
    dragState,
    dragCounter,
    handleDragStart,
    handleSidebarDragStart,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    resetDragState,
  } = useDragDrop(schema, moveField, addFieldToParent, setSchema);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && initialSchema.length > 0) {
      setSchema(initialSchema);
    }
  };

  const handleFieldSelect = (field: SchemaField) => {
    setSelectedField(field);
  };

  const handleFieldUpdate = (id: string, updates: Partial<SchemaField>) => {
    updateField(id, updates);
    if (selectedField?.id === id) {
      setSelectedField((prev) => (prev ? { ...prev, ...updates } : null));
    }
  };

  const handleFieldDelete = (id: string) => {
    deleteField(id);
    if (selectedField?.id === id) {
      setSelectedField(null);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(schema);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setSchema(initialSchema);
    setSelectedField(null);
    resetDragState();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Settings2 className="mr-2 h-4 w-4" />
            Edit Schema
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] min-w-max overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex h-[80vh]">
          <FieldTypePalette onDragStart={handleSidebarDragStart} />

          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-4 flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
                <Eye className="mr-2 h-4 w-4" />
                {showPreview ? "Hide" : "Show"} Code
              </Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>Save Schema</Button>
              </div>
            </div>

            <SchemaCanvas
              schema={schema}
              selectedField={selectedField}
              dragState={dragState}
              onFieldSelect={handleFieldSelect}
              onFieldUpdate={handleFieldUpdate}
              onFieldDelete={handleFieldDelete}
              onDragStart={handleDragStart}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onAddField={addField}
              dragCounter={dragCounter}
            />

            {showPreview && (
              <Card className="mt-4 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Generated Zod Schema
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="overflow-x-auto rounded-lg p-4 font-mono text-sm">
                    <code>{generateZodSchema(schema)}</code>
                  </pre>
                </CardContent>
              </Card>
            )}
          </div>

          {selectedField && (
            <PropertiesPanel
              selectedField={selectedField}
              onUpdateField={handleFieldUpdate}
              onUpdateArrayItemType={updateArrayItemType}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
